/**
 * Admin: Full SEO management with tabbed sections.
 * Covers Basic SEO, Open Graph, Twitter Card, and Advanced (schema/robots).
 */

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  listSEOOverrides,
  getSEOOverrideByPath,
  saveSEOOverride,
  deleteSEOOverride,
  getAllCMSPages,
  pathToSeoDocId,
  type SEOOverride,
} from '@/lib/cms';

const SITE_URL = 'https://smbprime.ai';
const DEFAULT_OG_IMAGE = `${SITE_URL}/social-share-smbprime.jpeg`;

/**
 * SEO defaults per known page path — pre-populated with real SMBPrime copy.
 * Auto-applied when the admin enters a matching path in the "New override" form.
 */
const PAGE_DEFAULTS: Record<string, Partial<SEOOverride>> = {
  '/': {
    title: 'SMBPrime — Federal Subcontracting Growth for Small Businesses',
    description:
      'SMBPrime secures qualified Prime Contractor meetings for established federal subcontractors. Human-led business development enhanced by AI for structured, predictable subcontracting growth.',
    keywords:
      'federal subcontracting, prime contractor meetings, small business government contracts, federal BD, subcontracting growth, AI business development, SMB federal contracting',
    ogTitle: 'SMBPrime — Federal Subcontracting Growth for Small Businesses',
    ogDescription:
      'We secure qualified Prime Contractor meetings so you can grow through federal subcontracting. Structured execution for established contractors — without a full internal BD team.',
    ogImage: DEFAULT_OG_IMAGE,
    twitterCard: 'summary_large_image',
    twitterTitle: 'SMBPrime — Federal Subcontracting Growth',
    twitterDescription:
      'Qualified Prime Contractor meetings. Human-led, AI-enhanced. Built for established federal subcontractors ready for disciplined growth.',
    canonical: `${SITE_URL}/`,
    noIndex: false,
    schemaJson: JSON.stringify(
      {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': `${SITE_URL}/#organization`,
            name: 'SMBPrime',
            url: SITE_URL,
            logo: {
              '@type': 'ImageObject',
              url: `${SITE_URL}/logo.png`,
            },
            description:
              'Government contracting made simple. SMBPrime helps small businesses win federal subcontracting opportunities through structured business development.',
            telephone: '+18325426260',
            sameAs: [
              'https://www.linkedin.com/company/smbprime/',
              'https://www.facebook.com/smbprime',
            ],
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+18325426260',
              contactType: 'sales',
              availableLanguage: 'English',
            },
          },
          {
            '@type': 'WebSite',
            '@id': `${SITE_URL}/#website`,
            url: SITE_URL,
            name: 'SMBPrime',
            description: 'Government contracting made simple for small businesses.',
            publisher: { '@id': `${SITE_URL}/#organization` },
          },
        ],
      },
      null,
      2
    ),
  },
  '/about-us': {
    title: 'About SMBPrime — Democratizing Federal Subcontracting for Small Businesses',
    description:
      'Meet the SMBPrime team. Founded to democratize federal subcontracting for SMBs, we combine AI-powered tools with expert guidance to connect small businesses with prime contractors.',
    keywords:
      'about SMBPrime, SMBPrime team, federal subcontracting company, AI subcontracting platform, small business government contracts leadership, subcontracting experts',
    ogTitle: 'About SMBPrime — Democratizing Federal Subcontracting',
    ogDescription:
      'Meet the team behind SMBPrime. Founded to democratize access to federal subcontracting opportunities for small and medium-sized businesses with AI-powered tools and expert guidance.',
    ogImage: DEFAULT_OG_IMAGE,
    twitterCard: 'summary_large_image',
    twitterTitle: 'About SMBPrime',
    twitterDescription:
      'Democratizing federal subcontracting for small businesses. Meet the team, mission, and leadership driving SMBPrime.',
    canonical: `${SITE_URL}/about-us`,
    noIndex: false,
    schemaJson: JSON.stringify(
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        '@id': `${SITE_URL}/about-us#aboutpage`,
        url: `${SITE_URL}/about-us`,
        name: 'About SMBPrime',
        description:
          'Learn about SMBPrime — our mission, leadership team, and how we help small businesses win federal subcontracting opportunities.',
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
      null,
      2
    ),
  },
  '/privacy-policy': {
    title: 'Privacy Policy | SMBPrime.ai',
    description:
      "SMBPrime's privacy policy explains how we collect, use, and protect your personal information when you use our AI-powered federal subcontracting platform.",
    keywords: 'SMBPrime privacy policy, data privacy, personal information, data protection',
    ogTitle: 'Privacy Policy | SMBPrime.ai',
    ogDescription:
      'SMBPrime is committed to protecting your privacy. Read our full privacy policy to understand how we handle your personal data.',
    twitterCard: 'summary',
    canonical: `${SITE_URL}/privacy-policy`,
    noIndex: false,
  },
  '/terms-of-service': {
    title: 'Terms of Service | SMBPrime.ai',
    description:
      "Review SMBPrime's terms of service. By using our AI-powered federal subcontracting platform, you agree to these terms and conditions governing your use of our services.",
    keywords: 'SMBPrime terms of service, user agreement, platform terms, terms and conditions',
    ogTitle: 'Terms of Service | SMBPrime.ai',
    ogDescription:
      "Read the SMBPrime terms of service to understand your rights and responsibilities when using our platform. Questions? Contact legal@smbprime.ai.",
    twitterCard: 'summary',
    canonical: `${SITE_URL}/terms-of-service`,
    noIndex: false,
  },
  '/dashboard': {
    title: 'Your Dashboard | SMBPrime',
    description:
      'Manage your federal subcontracting pipeline, track opportunities, and monitor your government contracting progress — all in one place.',
    keywords: 'SMBPrime dashboard, subcontracting pipeline, federal opportunities tracker',
    twitterCard: 'summary',
    canonical: `${SITE_URL}/dashboard`,
    noIndex: true,
  },
};

type FormTab = 'basic' | 'opengraph' | 'twitter' | 'advanced';

const TABS: { id: FormTab; label: string; hint?: string }[] = [
  { id: 'basic', label: 'Basic SEO' },
  { id: 'opengraph', label: 'Open Graph' },
  { id: 'twitter', label: 'Twitter / X' },
  { id: 'advanced', label: 'Advanced' },
];

interface SeoListItem {
  id: string;
  title?: string | null;
  path?: string | null;
  noIndex?: boolean | null;
}

interface AdminSEOCrudProps {
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
  /** Optional action (e.g. Help & guides) to show in the title row. */
  titleAction?: React.ReactNode;
}

function charCountColor(len: number, optimal: number, max: number): string {
  if (len === 0) return 'text-gray-400';
  if (len <= optimal) return 'text-emerald-600';
  if (len <= max) return 'text-amber-600';
  return 'text-red-600';
}

function charCountBar(len: number, max: number): number {
  return Math.min(100, Math.round((len / max) * 100));
}

function emptyForm(): SEOOverride {
  return {
    title: '',
    description: '',
    keywords: '',
    ogImage: '',
    ogTitle: '',
    ogDescription: '',
    twitterCard: 'summary_large_image',
    twitterTitle: '',
    twitterDescription: '',
    canonical: '',
    noIndex: false,
    schemaJson: '',
  };
}

export function AdminSEOCrud({ showNotify, confirmAction, titleAction }: AdminSEOCrudProps): JSX.Element {
  const [items, setItems] = useState<SeoListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newPath, setNewPath] = useState('');
  const [activeTab, setActiveTab] = useState<FormTab>('basic');
  const [form, setForm] = useState<SEOOverride>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [autoFilledPath, setAutoFilledPath] = useState<string | null>(null);
  const lastAutoFilledRef = useRef<string | null>(null);
  const [pathSuggestions, setPathSuggestions] = useState<{ path: string; label: string }[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const [list, cmsPages] = await Promise.all([
        listSEOOverrides(),
        getAllCMSPages(),
      ]);
      setItems(list);

      // Build path suggestions: static known paths + CMS pages with a path or slug
      const staticSuggestions: { path: string; label: string }[] = [
        { path: '/', label: '/ — Home' },
        { path: '/about', label: '/about — About' },
        { path: '/privacy', label: '/privacy — Privacy Policy' },
        { path: '/terms', label: '/terms — Terms of Service' },
        { path: '/dashboard', label: '/dashboard — Customer Dashboard' },
      ];

      const alreadyOverridden = new Set(
        list.map((item) => (item.id === 'home' ? '/' : `/${item.id.replace(/_/g, '/')}`))
      );
      const staticPaths = new Set(staticSuggestions.map((s) => s.path));

      const cmsSuggestions: { path: string; label: string }[] = cmsPages
        .map((p) => {
          const pagePath = p.path ?? (p.type === 'landing' ? `/lp/${p.slug}` : null);
          if (!pagePath) return null;
          return {
            path: pagePath,
            label: `${pagePath} — ${p.title} (${p.status})`,
          };
        })
        .filter((s): s is { path: string; label: string } => !!s && !staticPaths.has(s.path));

      const allSuggestions = [
        ...staticSuggestions,
        ...cmsSuggestions,
      ].map((s) => ({
        ...s,
        label: alreadyOverridden.has(s.path) ? `${s.label} ✓ has override` : s.label,
      }));

      setPathSuggestions(allSuggestions);
    } catch (err) {
      console.error('[AdminSEOCrud] Load error:', err);
      showNotify?.('Failed to load SEO overrides.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Auto-fill form with SMBPrime defaults when the path changes to a known route (new form only).
  useEffect(() => {
    if (editingId !== '__new__') return;
    const raw = newPath.trim();
    const normalized = raw === '' || raw === '/' ? '/' : raw.startsWith('/') ? raw : `/${raw}`;
    const defaults = PAGE_DEFAULTS[normalized];
    if (defaults && normalized !== lastAutoFilledRef.current) {
      lastAutoFilledRef.current = normalized;
      setAutoFilledPath(normalized);
      setForm({ ...emptyForm(), ...defaults });
    } else if (!defaults && lastAutoFilledRef.current !== null) {
      lastAutoFilledRef.current = null;
      setAutoFilledPath(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only react to path/editingId changes
  }, [newPath, editingId]);

  const openEdit = async (docId: string) => {
    setActiveTab('basic');
    setEditingId(docId);
    const path = docId === 'home' ? '/' : `/${docId.replace(/_/g, '/')}`;
    setLoadingForm(true);
    try {
      const data = await getSEOOverrideByPath(path);
      const defaultsForPath = PAGE_DEFAULTS[path];
      setForm({
        title: data?.title ?? defaultsForPath?.title ?? '',
        description: data?.description ?? defaultsForPath?.description ?? '',
        keywords: data?.keywords ?? defaultsForPath?.keywords ?? '',
        ogImage: data?.ogImage ?? defaultsForPath?.ogImage ?? '',
        ogTitle: data?.ogTitle ?? defaultsForPath?.ogTitle ?? '',
        ogDescription: data?.ogDescription ?? defaultsForPath?.ogDescription ?? '',
        twitterCard: data?.twitterCard ?? defaultsForPath?.twitterCard ?? 'summary_large_image',
        twitterTitle: data?.twitterTitle ?? defaultsForPath?.twitterTitle ?? '',
        twitterDescription: data?.twitterDescription ?? defaultsForPath?.twitterDescription ?? '',
        canonical: data?.canonical ?? defaultsForPath?.canonical ?? '',
        noIndex: data?.noIndex ?? defaultsForPath?.noIndex ?? false,
        schemaJson: data?.schemaJson ?? defaultsForPath?.schemaJson ?? '',
      });
    } catch (err) {
      console.error('[AdminSEOCrud] Failed to load SEO override from database:', err);
      showNotify?.('Failed to load SEO override from database.', 'error');
      const defaultsForPath = PAGE_DEFAULTS[path];
      setForm(defaultsForPath ? { ...emptyForm(), ...defaultsForPath } : emptyForm());
    } finally {
      setLoadingForm(false);
    }
  };

  const handleSave = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      let docId = editingId;
      let path = editingId === 'home' ? '/' : `/${editingId.replace(/_/g, '/')}`;
      if (editingId === '__new__') {
        const p = newPath.trim() || '/';
        path = p.startsWith('/') ? p : `/${p}`;
        docId = pathToSeoDocId(path);
      }
      await saveSEOOverride(docId, form, path);
      showNotify?.(
        'SEO override saved. Developer action required: run `npm run seo:generate-index`, then `npm run build`, then `firebase deploy --only hosting`.',
        'success'
      );
      setEditingId(null);
      setNewPath('');
      void load();
    } catch (err) {
      console.error('[AdminSEOCrud] Save error:', err);
      showNotify?.('Failed to save SEO override.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (docId: string) => {
    const pathLabel = docId === 'home' ? '/' : `/${docId.replace(/_/g, '/')}`;
    const doDelete = async () => {
      try {
        await deleteSEOOverride(docId);
        showNotify?.('SEO override deleted.');
        if (editingId === docId) setEditingId(null);
        void load();
      } catch (err) {
        console.error('[AdminSEOCrud] Delete error:', err);
        showNotify?.('Failed to delete.', 'error');
      }
    };
    if (confirmAction) {
      confirmAction(
        'Delete SEO override?',
        `Remove the SEO override for "${pathLabel}"? This cannot be undone.`,
        doDelete
      );
    } else {
      void doDelete();
    }
  };

  const addNew = () => {
    setActiveTab('basic');
    lastAutoFilledRef.current = null;
    setAutoFilledPath(null);
    setEditingId('__new__');
    setNewPath('/');
    setForm({ ...emptyForm(), ...PAGE_DEFAULTS['/'] });
    lastAutoFilledRef.current = '/';
    setAutoFilledPath('/');
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-gray-500">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        <span>Loading SEO overrides…</span>
      </div>
    );
  }

  const titleLen = (form.title ?? '').length;
  const descLen = (form.description ?? '').length;
  const editPathLabel =
    editingId === '__new__'
      ? newPath || '/'
      : editingId === 'home'
      ? '/'
      : `/${editingId?.replace(/_/g, '/')}`;

  return (
    <div className="space-y-6">
      {/* ─── Header ─── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">SEO Management</h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Manage title tags, meta descriptions, Open Graph, Twitter Cards, structured data, and robots directives per page.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {titleAction}
          {!editingId && (
            <button
              type="button"
              onClick={addNew}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              + Add page override
            </button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border-2 border-amber-300 bg-amber-50 shadow-sm">
        <div className="flex items-start gap-3 border-b border-amber-200 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Developer Publish Required</p>
            <p className="mt-0.5 text-xs text-amber-800">
              SEO changes are saved to the database, but crawlers read deployed static HTML. A developer must publish changes after every SEO update.
            </p>
          </div>
        </div>
        <div className="space-y-2 px-4 py-3">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-amber-900">
            <li>Save your SEO changes here.</li>
            <li>Ask developer to run <code className="rounded bg-amber-100 px-1 py-0.5">npm run seo:generate-index</code>.</li>
            <li>Then run <code className="rounded bg-amber-100 px-1 py-0.5">npm run build</code>.</li>
            <li>Then deploy with <code className="rounded bg-amber-100 px-1 py-0.5">firebase deploy --only hosting</code>.</li>
            <li>Validate using LinkedIn Post Inspector after deploy.</li>
          </ol>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href="https://www.linkedin.com/post-inspector/inspect/https:%2F%2Fsmbprime.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm hover:bg-amber-100"
            >
              Open LinkedIn Post Inspector
            </a>
            <a
              href="https://developers.facebook.com/tools/debug/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm hover:bg-amber-100"
            >
              Open Facebook Sharing Debugger
            </a>
          </div>
        </div>
      </div>

      {/* ─── Edit / New Form ─── */}
      {editingId && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {/* Form header */}
          <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/80 px-6 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">SEO Override</p>
              <h3 className="mt-0.5 font-semibold text-gray-900">
                {editingId === '__new__' ? 'New override' : `Editing: ${editPathLabel}`}
              </h3>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || loadingForm}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => { setEditingId(null); setAutoFilledPath(null); lastAutoFilledRef.current = null; setLoadingForm(false); }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>

          {loadingForm && editingId !== '__new__' && (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
              <span className="text-sm">Loading override from database…</span>
            </div>
          )}

          {(!loadingForm || editingId === '__new__') && (
          <>
          {/* Path field (new only) */}
          {editingId === '__new__' && (
            <div className="border-b border-gray-100 px-6 py-4 space-y-3">
              <div>
                <label htmlFor="seo-path-input" className="block text-sm font-medium text-gray-700">
                  Page Path <span className="text-red-500">*</span>
                </label>
                <div className="relative mt-1.5">
                  <input
                    id="seo-path-input"
                    type="text"
                    list="seo-path-suggestions"
                    value={newPath}
                    onChange={(e) => setNewPath(e.target.value)}
                    placeholder="/ or /about or /lp/promo"
                    autoComplete="off"
                    className="w-full rounded-lg border border-gray-300 py-2 pl-3 pr-9 font-mono text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <datalist id="seo-path-suggestions">
                    {pathSuggestions.map((s) => (
                      <option key={s.path} value={s.path}>{s.label}</option>
                    ))}
                  </datalist>
                  <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-gray-400" aria-hidden>
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Pick a page from the list or type any custom path. Use <code className="rounded bg-gray-100 px-1">/</code> for home.
                </p>
              </div>
              {autoFilledPath && (
                <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-600 text-sm">✓</span>
                    <p className="text-xs text-emerald-700 font-medium">
                      SMBPrime defaults loaded for <code className="rounded bg-emerald-100 px-1">{autoFilledPath}</code>
                      <span className="font-normal text-emerald-600"> — review and customize as needed.</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setForm(emptyForm()); setAutoFilledPath(null); lastAutoFilledRef.current = '__cleared__'; }}
                    className="ml-3 shrink-0 text-xs text-emerald-600 underline hover:text-emerald-800"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="-mb-px flex gap-0 border-b border-gray-200 px-6">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="space-y-5 px-6 py-5">

            {/* ── BASIC SEO ── */}
            {activeTab === 'basic' && (
              <>
                {/* Title */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">Page Title</label>
                    <span className={`text-xs font-mono ${charCountColor(titleLen, 60, 70)}`}>
                      {titleLen} / 60
                    </span>
                  </div>
                  <input
                    type="text"
                    value={form.title ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Government Contracting for Small Businesses | SMBPrime"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full transition-all ${
                        titleLen === 0 ? 'bg-gray-200' : titleLen <= 60 ? 'bg-emerald-400' : titleLen <= 70 ? 'bg-amber-400' : 'bg-red-400'
                      }`}
                      style={{ width: `${charCountBar(titleLen, 70)}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">Optimal: 50–60 characters. Appears in browser tabs and Google search results.</p>
                </div>

                {/* Meta Description */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">Meta Description</label>
                    <span className={`text-xs font-mono ${charCountColor(descLen, 160, 180)}`}>
                      {descLen} / 160
                    </span>
                  </div>
                  <textarea
                    value={form.description ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    rows={3}
                    placeholder="e.g. SMBPrime helps small businesses discover, bid on, and win federal contracts with AI-powered tools."
                    className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full transition-all ${
                        descLen === 0 ? 'bg-gray-200' : descLen <= 160 ? 'bg-emerald-400' : descLen <= 180 ? 'bg-amber-400' : 'bg-red-400'
                      }`}
                      style={{ width: `${charCountBar(descLen, 180)}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">Optimal: 150–160 characters. Shown in Google search result snippets.</p>
                </div>

                {/* Keywords */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Meta Keywords</label>
                  <textarea
                    rows={3}
                    value={form.keywords ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))}
                    placeholder="e.g. federal contracts, small business, government bids, AI proposals"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-y min-h-[80px]"
                  />
                  <p className="mt-1 text-xs text-gray-400">Comma-separated. Low direct SEO weight but useful for topical categorization and internal search.</p>
                </div>

                {/* Canonical URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Canonical URL</label>
                  <input
                    type="text"
                    value={form.canonical ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, canonical: e.target.value }))}
                    placeholder="https://smbprime.ai/about-us"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">Tells search engines the preferred URL for this page. Leave blank to use the default page URL.</p>
                </div>

                {/* No Index toggle */}
                <div className="flex items-start gap-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">No Index</p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Adds <code className="rounded bg-amber-100 px-1 py-px text-amber-800">{'<meta name="robots" content="noindex, nofollow">'}</code> to prevent search engines from indexing this page.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, noIndex: !f.noIndex }))}
                    className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                      form.noIndex ? 'bg-amber-500' : 'bg-gray-200'
                    }`}
                    aria-pressed={form.noIndex ?? false}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        form.noIndex ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Google SERP Preview */}
                {(form.title || form.description) && (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Google Search Preview</p>
                    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                      <p className="text-xs text-green-700">smbprime.ai › {editPathLabel.replace(/^\//, '') || ''}</p>
                      <p className="text-base font-medium text-blue-700 hover:underline cursor-pointer truncate">
                        {form.title || 'Page title'}
                      </p>
                      <p className="mt-0.5 text-sm text-gray-600 line-clamp-2">
                        {form.description || 'Page meta description will appear here…'}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── OPEN GRAPH ── */}
            {activeTab === 'opengraph' && (
              <>
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                  Open Graph tags control how your page appears when shared on <strong>Facebook, LinkedIn, Slack,</strong> and other platforms that support OG.
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">OG Title</label>
                  <input
                    type="text"
                    value={form.ogTitle ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, ogTitle: e.target.value }))}
                    placeholder={form.title || 'Defaults to Page Title'}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">Leave blank to inherit the Page Title.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">OG Description</label>
                  <textarea
                    value={form.ogDescription ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, ogDescription: e.target.value }))}
                    rows={3}
                    placeholder={form.description || 'Defaults to Meta Description'}
                    className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">Leave blank to inherit the Meta Description.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">OG Image URL</label>
                  <input
                    type="text"
                    value={form.ogImage ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, ogImage: e.target.value }))}
                    placeholder="https://smbprime.ai/social-share-smbprime.jpeg"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">Recommended size: <strong>1200 × 630 px</strong>. Shown when the page is shared on social media.</p>
                </div>

                {/* Social preview card */}
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Social Share Preview</p>
                  <div className="max-w-sm overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                    {form.ogImage ? (
                      <img
                        src={form.ogImage}
                        alt="OG preview"
                        className="h-36 w-full bg-gray-100 object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="flex h-36 w-full items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-100 text-sm text-gray-400">
                        No image set
                      </div>
                    )}
                    <div className="border-t border-gray-100 bg-white px-3 py-2">
                      <p className="text-xs uppercase text-gray-400">smbprime.ai</p>
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {form.ogTitle || form.title || 'Page title'}
                      </p>
                      <p className="line-clamp-2 text-xs text-gray-500">
                        {form.ogDescription || form.description || 'Page description'}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ── TWITTER / X ── */}
            {activeTab === 'twitter' && (
              <>
                <div className="rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-700">
                  Twitter Card tags control how your page appears when shared on <strong>X (Twitter)</strong>.
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Card Type</label>
                  <select
                    value={form.twitterCard ?? 'summary_large_image'}
                    onChange={(e) => setForm((f) => ({ ...f, twitterCard: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="summary_large_image">Summary with Large Image (recommended)</option>
                    <option value="summary">Summary</option>
                    <option value="app">App</option>
                    <option value="player">Player</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-400"><strong>Summary with Large Image</strong> is recommended for marketing and content pages.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Twitter Title</label>
                  <input
                    type="text"
                    value={form.twitterTitle ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, twitterTitle: e.target.value }))}
                    placeholder={form.ogTitle || form.title || 'Defaults to OG Title or Page Title'}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">Leave blank to inherit OG Title or Page Title.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Twitter Description</label>
                  <textarea
                    value={form.twitterDescription ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, twitterDescription: e.target.value }))}
                    rows={3}
                    placeholder={form.ogDescription || form.description || 'Defaults to OG Description or Meta Description'}
                    className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">Leave blank to inherit OG Description or Meta Description.</p>
                </div>

                {/* Twitter card preview */}
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">X / Twitter Card Preview</p>
                  <div className="max-w-sm overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
                    {form.ogImage && (
                      <img
                        src={form.ogImage}
                        alt="Twitter card preview"
                        className="h-36 w-full bg-gray-100 object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <div className="bg-white px-3 py-2">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {form.twitterTitle || form.ogTitle || form.title || 'Page title'}
                      </p>
                      <p className="line-clamp-2 text-xs text-gray-500">
                        {form.twitterDescription || form.ogDescription || form.description || 'Page description'}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">smbprime.ai</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ── ADVANCED ── */}
            {activeTab === 'advanced' && (
              <>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                  <strong>Structured data (JSON-LD)</strong> helps search engines understand your page content and can enable rich results in Google (e.g. organization cards, FAQs, breadcrumbs).
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Schema / Structured Data
                      <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-px text-xs text-gray-500">JSON-LD</span>
                    </label>
                    <a
                      href="https://validator.schema.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Validate →
                    </a>
                  </div>
                  <textarea
                    value={form.schemaJson ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, schemaJson: e.target.value }))}
                    rows={12}
                    spellCheck={false}
                    placeholder={`{\n  "@context": "https://schema.org",\n  "@type": "Organization",\n  "name": "SMBPrime",\n  "url": "https://smbprime.ai",\n  "description": "Government contracting made simple for small businesses.",\n  "sameAs": ["https://linkedin.com/company/smbprime"]\n}`}
                    className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Injected as <code className="rounded bg-gray-100 px-1">{'<script type="application/ld+json">'}</code> in the page head. Use the validator to check for errors before saving.
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Common Schema Types for SMBPrime</p>
                  <ul className="space-y-1 text-xs text-gray-500">
                    <li><code className="text-gray-700">Organization</code> — Company info, logo, contact, social profiles</li>
                    <li><code className="text-gray-700">WebSite</code> — Site name and search action</li>
                    <li><code className="text-gray-700">FAQPage</code> — FAQ section for rich results</li>
                    <li><code className="text-gray-700">BreadcrumbList</code> — Navigation breadcrumbs</li>
                    <li><code className="text-gray-700">SoftwareApplication</code> — App pages with ratings/pricing</li>
                  </ul>
                </div>
              </>
            )}
          </div>
          </>
          )}
        </div>
      )}

      {/* ─── Page List ─── */}
      {!editingId && (
        <>
          {items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 py-14 text-center">
              <p className="text-gray-500 text-sm font-medium">No SEO overrides yet.</p>
              <p className="mt-1 text-xs text-gray-400">
                Add an override to customize meta tags, Open Graph, and structured data for any page.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Path</th>
                    <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 sm:table-cell">Title</th>
                    <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 md:table-cell">Robots</th>
                    <th className="w-28 px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const pathLabel = item.id === 'home' ? '/' : `/${item.id.replace(/_/g, '/')}`;
                    return (
                      <tr
                        key={item.id}
                        className={`border-b border-gray-100 last:border-0 ${idx % 2 !== 0 ? 'bg-gray-50/40' : ''}`}
                      >
                        <td className="px-4 py-3 font-mono text-xs text-gray-700">{pathLabel}</td>
                        <td className="hidden max-w-xs truncate px-4 py-3 text-gray-500 sm:table-cell">
                          {item.title || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="hidden px-4 py-3 md:table-cell">
                          {item.noIndex ? (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">noindex</span>
                          ) : (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-600">index</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              type="button"
                              onClick={() => openEdit(item.id)}
                              className="text-xs font-medium text-blue-600 hover:text-blue-800"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item.id)}
                              className="text-xs font-medium text-red-500 hover:text-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
